import { useCallback, useEffect, useRef, useState } from "react";

import { connect, useSelector } from "react-redux";

import { Spinner } from "@ps-design-system/components/Spinner";

import { hot } from "hot";

import MyListings from "../../../app-mylistings/MyListings";
import { useStatsigContext } from "../../../elements-statsig-provider/utils";
import { fetchEnvWeb } from "../../actions/env";
import { fetchOwnerListingsBySpaceIds } from "../../actions/listings";
import { updateExperience } from "../../actions/nux";
import { fetchOwnerSpacesInfo } from "../../actions/spaces";
import { fetchWorkflows } from "../../actions/workflows";
import { selectProfileIsVendor } from "../../selectors/profile.selectors";

import {
  EDIT_PRICE_EXPERIENCE,
  MANAGE_ADDONS_EXPERIENCE,
  NEW_BADGE_EXPERIENCE,
  PAGE_SIZE,
} from "./MyListingsContainer.consts";
import type {
  MyListingsContainerProps,
  MyListingsContainerState,
  MyListingsFeatureGates,
} from "./MyListingsContainer.types";
import {
  buildSpacesQuery,
  getActiveNuxExperienceId,
  getCurrentPage,
  getTotalPages,
} from "./MyListingsContainer.utils";

// Props are taken as a single object rather than destructured in the signature
// because they're forwarded wholesale to `MyListings`, which passes them on to
// its banners and modals. Destructuring here would silently drop those.
const MyListingsContainerComponent = (props: MyListingsContainerProps) => {
  const { accessToken, dispatch, isFetching, location, spaces, SSOid } = props;
  // getting statsig gate for vendor editing — the Chicago-metro restriction only
  // applies to hosts; vendor-only users (no spaces) aren't subject to it.
  const { initialized, featureGates } = useStatsigContext();

  const isVendor = useSelector(selectProfileIsVendor);

  const currentPage = getCurrentPage(location);
  const totalSpaces = spaces.total || 0;
  const totalPages = getTotalPages(totalSpaces);
  const nuxExperiences = props.nux?.experiences;
  const isFetchingProfile = Boolean(props.profile.isFetching);

  const pageSpaceIdsKey = (spaces.data ?? [])
    .map((space) => space._id)
    .join(",");
  // Which page of spaces the listings in the store were last fetched for.
  const [fetchedListingsKey, setFetchedListingsKey] = useState<string | null>(
    null,
  );
  // Gates the spinner. With no spaces there's nothing to fetch, so it's fetched.
  const hasFetchedPageListings =
    !pageSpaceIdsKey || fetchedListingsKey === pageSpaceIdsKey;

  const hasChicagoSpace = (spaces.data ?? []).some(
    (space) => "CHI" === space.metro,
  );

  // The listings/spaces fetch and the NUX badge fire on transitions, so the
  // first render has nothing to compare against and must not dispatch.
  const wasFetchingProfile = useRef(isFetchingProfile);
  const previousPage = useRef(currentPage);
  const hadNuxExperiences = useRef(Boolean(nuxExperiences?.length));

  const handlePageChange = useCallback(
    (page: number) => {
      props.router?.push({
        pathname: location?.pathname ?? "",
        query: { ...(location?.query ?? {}), page },
      });
      if ("undefined" !== typeof window) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [location, props.router],
  );

  const updateNux = useCallback(
    (experienceName: string) => {
      const nuxExperienceId = getActiveNuxExperienceId({
        experienceName,
        experiences: nuxExperiences,
      });
      if (nuxExperienceId) {
        dispatch(updateExperience(nuxExperienceId));
      }
    },
    [dispatch, nuxExperiences],
  );

  useEffect(() => {
    dispatch(fetchEnvWeb());
  }, [dispatch]);

  // Initial load: fetch the first page of spaces once the profile resolves.
  useEffect(() => {
    if (wasFetchingProfile.current && !isFetchingProfile && SSOid) {
      dispatch(
        fetchOwnerSpacesInfo(
          buildSpacesQuery({ page: currentPage, SSOid }),
          accessToken,
        ),
      );
      dispatch(fetchWorkflows(`host/${SSOid}`, accessToken)).catch(() => {});
    }

    wasFetchingProfile.current = isFetchingProfile;
  }, [accessToken, currentPage, dispatch, isFetchingProfile, SSOid]);

  // Page navigation: refetch spaces when the ?page query changes.
  useEffect(() => {
    if (previousPage.current !== currentPage && SSOid) {
      dispatch(
        fetchOwnerSpacesInfo(
          buildSpacesQuery({ page: currentPage, SSOid }),
          accessToken,
        ),
      );
    }

    previousPage.current = currentPage;
  }, [accessToken, currentPage, dispatch, SSOid]);

  // Listings are fetched only for the spaces on the current page.
  useEffect(() => {
    if (!SSOid || !spaces.hasFetched || spaces.isFetching || !pageSpaceIdsKey) {
      return undefined;
    }

    // Set on cleanup, so a late response from a previous run (e.g. the page
    // the host just left) can't mark the current page as fetched.
    let isCancelled = false;
    // A failed fetch still ends the spinner; the panels render without listings.
    const markPageListingsFetched = () => {
      if (!isCancelled) {
        setFetchedListingsKey(pageSpaceIdsKey);
      }
    };
    dispatch(
      fetchOwnerListingsBySpaceIds({
        SSOid,
        spaceIds: pageSpaceIdsKey.split(","),
      }),
    ).then(markPageListingsFetched, markPageListingsFetched);

    return () => {
      isCancelled = true;
    };
  }, [dispatch, pageSpaceIdsKey, spaces.hasFetched, spaces.isFetching, SSOid]);

  // Snap back to the last valid page if the URL points past the end — e.g. an
  // out-of-range ?page, or the last space on the last page was just deleted.
  // Guarded to settled data so it doesn't loop mid-fetch.
  useEffect(() => {
    if (
      spaces.hasFetched &&
      !spaces.isFetching &&
      0 < totalSpaces &&
      currentPage > totalPages
    ) {
      handlePageChange(totalPages);
    }
  }, [
    currentPage,
    handlePageChange,
    spaces.hasFetched,
    spaces.isFetching,
    totalPages,
    totalSpaces,
  ]);

  useEffect(() => {
    const hasNuxExperiences = Boolean(nuxExperiences?.length);
    if (!hadNuxExperiences.current && hasNuxExperiences) {
      updateNux(NEW_BADGE_EXPERIENCE);
    }

    hadNuxExperiences.current = hasNuxExperiences;
  }, [nuxExperiences, updateNux]);

  // if the user is a host they will get either a true/false from the hasChicagoSpace
  // isVendor catches users who are vendors but not hosts
  // if one of these is true then we will check the feature gate
  const allowVendorEdits =
    isVendor || hasChicagoSpace
      ? ((featureGates as MyListingsFeatureGates)?.allow_vendor_edits ?? false)
      : false;

  const isLoading =
    isFetching ||
    spaces.isFetching ||
    !spaces.hasFetched ||
    !hasFetchedPageListings ||
    !initialized;

  if (isLoading) {
    return (
      <div className="tw-h-full tw-w-full tw-flex tw-flex-1 tw-items-center tw-justify-center">
        <Spinner className="tw-size-28 tw-m-24" appearance="gray" />
      </div>
    );
  }

  return (
    <MyListings
      {...props}
      allowVendorEdits={allowVendorEdits}
      showTooltip={Boolean(
        getActiveNuxExperienceId({
          experienceName: EDIT_PRICE_EXPERIENCE,
          experiences: nuxExperiences,
        }),
      )}
      showAddonsTooltip={Boolean(
        getActiveNuxExperienceId({
          experienceName: MANAGE_ADDONS_EXPERIENCE,
          experiences: nuxExperiences,
        }),
      )}
      updateNux={updateNux}
      currentPage={currentPage}
      totalPages={totalPages}
      totalSpaces={totalSpaces}
      pageSize={PAGE_SIZE}
      onPageChange={handlePageChange}
    />
  );
};

const mapStateToProps = (state: MyListingsContainerState) => {
  const {
    auth,
    profile,
    spaces,
    nux,
    env,
    modal,
    workflows,
    nav: { managedAccount },
  } = state;

  return {
    auth,
    profile,
    SSOid: profile.SSOid,
    accessToken: auth.accessToken,
    spaces,
    isFetching: Boolean(profile.isFetching),
    nux,
    env,
    modal,
    workflows,
    managedAccount,
  };
};

// `hot` resolves to `hot-reloading/production-hot.js` (an identity wrapper) for
// every Vite build, and that implementation takes no arguments — the `module`
// argument the .jsx containers still pass is a webpack/react-hot-loader leftover.
export default connect(mapStateToProps)(hot()(MyListingsContainerComponent));
